import axiosInstance from '../utils/axiosConfig'
import { mobileBackendUrl } from '../env'
import { EliteOnboardingStatusResponse, FamilyEliteDetailsResponse, UserDetailsResponse } from '../types/misc.types'
import type { ElitePricingDetailsResponse } from '../types/elitePricingDetails.types'
import type { ElitePrimePricingDetailsResponse } from '../types/elitePrimeSummary.types'

/** v2 advisory body may be the payload directly or wrapped as `{ data: payload }`. */
export function parseEliteOnboardingAdvisoryResponsePayload(
  httpBody: unknown
): EliteOnboardingStatusResponse | undefined {
  if (!httpBody || typeof httpBody !== 'object') {
    return undefined
  }
  const root = httpBody as Record<string, unknown>
  if (typeof root?.eliteLandingPageState === 'string' && Array.isArray(root?.eligiblePlans)) {
    return httpBody as EliteOnboardingStatusResponse
  }
  const nested = root?.data
  if (nested && typeof nested === 'object') {
    const inner = nested as Record<string, unknown>
    if (typeof inner?.eliteLandingPageState === 'string' && Array.isArray(inner?.eligiblePlans)) {
      return nested as EliteOnboardingStatusResponse
    }
  }
  return undefined
}

export function selectEliteOnboardingFromAdvisoryAxios(response: { data?: unknown } | undefined) {
  return parseEliteOnboardingAdvisoryResponsePayload(response?.data)
}

const getFromSearchParam = () => {
  let params: URLSearchParams | null = null
  if (window?.location?.search) params = new URLSearchParams(window.location.search)
  return params ? params.get('from') : ''
}

export const fetchOnboardingAdvisoryStatus = async (): Promise<EliteOnboardingStatusResponse> => {
  const from = getFromSearchParam()
  return axiosInstance.get(`${mobileBackendUrl}/v1/onboarding/advisory/status${from ? `?from=${from}` : ''}`)
}

export const fetchOnboardingAdvisoryStatusV2 = async (): Promise<EliteOnboardingStatusResponse> => {
  const from = getFromSearchParam()
  return axiosInstance.get(`${mobileBackendUrl}/v2/onboarding/advisory/status${from ? `?from=${from}` : ''}`)
}

export const fetchEliteSummaryStatus = async (): Promise<{ data?: unknown }> => {
  return axiosInstance.get(`${mobileBackendUrl}/v2/elite/summary`)
}

export const fetchElitePrimeSummaryStatus = async (): Promise<{ data?: unknown }> => {
  const from = getFromSearchParam()
  return axiosInstance.get(`${mobileBackendUrl}/v2/onboarding/advisory/prime-status${from ? `?from=${from}` : ''}`)
}

/** Records Elite Prime consent from the Prime landing page; called before the Prime CTA when `isPrimeConsent` is set. */
export const grantElitePrimeConsent = async (): Promise<{ data?: unknown }> => {
  return axiosInstance.post(`${mobileBackendUrl}/v2/consent/grant-consent`, {
    postBackParams: {
      consentType: 'ELITE_PRIME_CONSENT',
      consentVersion: 1,
      consentFormLocation: 'PRIME_LANDING_CONSENT'
    }
  })
}

const MAX_PRICING_DETAILS_ENVELOPE_DEPTH = 3

function isElitePrimePricingDetailsPayload(value: unknown): value is ElitePrimePricingDetailsResponse {
  if (!value || typeof value !== 'object') {
    return false
  }

  const candidate = value as Record<string, unknown>
  return Array.isArray(candidate.portfolioSlabList) || Array.isArray(candidate.fundAdditionalChargesDetails)
}

export function parseElitePrimePricingDetailsPayload(httpBody: unknown): ElitePrimePricingDetailsResponse | undefined {
  let candidate = httpBody

  for (let depth = 0; depth < MAX_PRICING_DETAILS_ENVELOPE_DEPTH; depth++) {
    if (isElitePrimePricingDetailsPayload(candidate)) {
      return candidate
    }
    if (!candidate || typeof candidate !== 'object') {
      return undefined
    }
    candidate = (candidate as Record<string, unknown>).data
  }

  return undefined
}

export function selectElitePrimePricingDetails(response: { data?: unknown } | undefined) {
  return parseElitePrimePricingDetailsPayload(response?.data)
}

export const fetchElitePrimePricingDetails = async (): Promise<{ data?: unknown }> => {
  const from = getFromSearchParam()
  return axiosInstance.get(
    `${mobileBackendUrl}/v2/portfolio/elite-prime-pricing-details${from ? `?from=${from}` : ''}`
  )
}

function isElitePricingDetailsPayload(value: unknown): value is ElitePricingDetailsResponse {
  if (!value || typeof value !== 'object') {
    return false
  }
  return Array.isArray((value as Record<string, unknown>).portfolioSlabList)
}

export function parseElitePricingDetailsPayload(httpBody: unknown): ElitePricingDetailsResponse | undefined {
  let candidate = httpBody

  for (let depth = 0; depth < MAX_PRICING_DETAILS_ENVELOPE_DEPTH; depth++) {
    if (isElitePricingDetailsPayload(candidate)) {
      return candidate
    }
    if (!candidate || typeof candidate !== 'object') {
      return undefined
    }
    candidate = (candidate as Record<string, unknown>).data
  }

  return undefined
}

export function selectElitePricingDetails(response: { data?: unknown } | undefined) {
  return parseElitePricingDetailsPayload(response?.data)
}

export const fetchElitePricingDetails = async (): Promise<{ data?: unknown }> => {
  const from = getFromSearchParam()
  return axiosInstance.get(
    `${mobileBackendUrl}/v2/portfolio/elite-pricing-details${from ? `?from=${from}` : ''}`
  )
}

export const fetchUserDetails = async (): Promise<UserDetailsResponse> => {
  const response = await axiosInstance.get(`${mobileBackendUrl}/v2/user/details`)
  if (!response?.data) {
    throw new Error('Failed to fetch user details')
  }
  return response?.data as UserDetailsResponse
}

export const fetchFamilyEliteDetails = async (): Promise<FamilyEliteDetailsResponse> => {
  const from = getFromSearchParam()
  const response = await axiosInstance.get(
    `${mobileBackendUrl}/v1/onboarding/advisory/status${from ? `?from=${from}` : ''}`
  )
  if (!response?.data) {
    throw new Error('Failed to fetch family elite details')
  }
  return response?.data as FamilyEliteDetailsResponse
}

export const fetchUsersWrap = async (): Promise<any> => {
  const response = await axiosInstance.get(`${mobileBackendUrl}/v2/portfolio/users-wrap`)
  if (!response?.data) {
    throw new Error('Failed to fetch users wrap data')
  }
  return response?.data
}

export const fetchMFCRedirectionData = async (): Promise<any> => {
  return axiosInstance.post(`${mobileBackendUrl}/v2/portfolio/mfc-fetch-callback`, {
    url: window.location.href
  })
}
